class KeyvaultKey {
    name: string;
    references: KeyvaultKey.Reference[];

    constructor(name: string) {
        this.name = name;
        this.references = [];
    }

    static from_json(input: any): KeyvaultKey {
        const keyvault_key = new KeyvaultKey(input.name);
        keyvault_key.references = input.references.map(ref => KeyvaultKey.Reference.from_json(ref))
        return keyvault_key;
    }
}

namespace KeyvaultKey {
    export class Reference {
        namespace: string;
        application: { kind: string, name: string };
        container: { kind: string, name: string };
        secret: { kind: string, name: string };

        constructor(namespace: string, application: { kind: string, name: string }, container: { kind: string, name: string }, secret: { kind: string, name: string }) {
            this.namespace = namespace;
            this.application = application;
            this.container = container;
            this.secret = secret;
        }

        static from_json(input: any): Reference {
            return new Reference(input.namespace, input.application, input.container, {
                kind: input.kind,
                name: input.name
            });
        }
    }
}

class Keyvault {
    tenant_id: string;
    name: string;
    keys: { [key: string]: KeyvaultKey };

    constructor(tenant_id: string, name: string) {
        this.tenant_id = tenant_id;
        this.name = name;
        this.keys = {};
    }

    static from_json(input): Keyvault {
        const keyvault = new Keyvault(input.tenant_id, input.name);
        for (const [ key, value] of Object.entries(input.keys)) {
            keyvault.keys[key] = KeyvaultKey.from_json(value);
        }
        return keyvault;
    }
}

class SecretProviderKeyRef {
    secret_provider_name: string = '';
    secret_provider_key: string = '';

    constructor(name, key) {
        this.secret_provider_name = name;
        this.secret_provider_key = key;
    }
}

class KeyvaultKeyRef {
    tenant_id: string = '';
    keyvault_name: string = '';
    keyvault_key: string = '';

    constructor(tenant_id, name, key) {
        this.tenant_id = tenant_id;
        this.keyvault_name = name;
        this.keyvault_key = key;
    }
}

class SecretKeyRef {
    secret_name: string = '';
    secret_key: string = '';
}

class HistoryEntry {
    date: string;
    reason: string;
    by: string;

    constructor(date: string, reason: string, by: string) {
        this.date = date;
        this.reason = reason;
        this.by = by;
    }

    static from_json(input: any): HistoryEntry {
        return new HistoryEntry(input.date, input.reason, input.by);
    }
}

class UsageEntry {
    environment: string;
    application: string;
    container: string;
    variable: string;

    constructor(environment: string, application: string, container: string, variable: string) {
        this.environment = environment;
        this.application = application;
        this.container = container;
        this.variable = variable;
    }

    static from_json(input: any): UsageEntry {
        return new UsageEntry(input.environment, input.application, input.container, input.variable);
    }
}


class SecretVariable {
    name: string = '';
    secret_provider_key_ref: SecretProviderKeyRef;
    keyvault_key_ref: KeyvaultKeyRef;
    secret_key_ref: SecretKeyRef;

    constructor(name, secret_provider_key_ref, keyvault_key_ref, secret_key_ref) {
        this.name = name;
        this.secret_provider_key_ref = secret_provider_key_ref;
        this.keyvault_key_ref = keyvault_key_ref;
        this.secret_key_ref = secret_key_ref;
    }

    static from_json(input): SecretVariable {
        const secret_provider_key_ref = new SecretProviderKeyRef(input.secret_provider.name, input.secret_provider.key);
        const keyvault_key_ref = new KeyvaultKeyRef(input.keyvault.tenant, input.keyvault.name, input.keyvault.key);
        const secret_key_ref = new SecretKeyRef();
        return new SecretVariable(input.name, secret_provider_key_ref, keyvault_key_ref, secret_key_ref);
    }
}

class SecretFile {
    name: string;
    keyvault_key_ref: KeyvaultKeyRef;
    secret_provider_key_ref: SecretProviderKeyRef;

    constructor(name, keyvault_key_ref, secret_provider_key_ref) {
        this.name = name;
        this.keyvault_key_ref = keyvault_key_ref;
        this.secret_provider_key_ref = secret_provider_key_ref;
    }

    static from_json(input): SecretFile {
        const secret_provider_key_ref = new SecretProviderKeyRef(input.secret_provider.name, input.secret_provider.key);
        const keyvault_key_ref = new KeyvaultKeyRef(input.keyvault.tenant, input.keyvault.name, input.keyvault.key);

        return new SecretFile(input.name, keyvault_key_ref, secret_provider_key_ref)
    }
}

class Container {
    name: string;
    kind: Container.Kind;
    secret_files: SecretFile[] = [];
    secret_variables: SecretVariable[] = [];

    constructor(name, kind) {
        this.name = name;
        this.kind = kind;
    }

    static from_json(input): Container {
        const container = new Container(input.name, input.kind);
        container.secret_variables = input.secrets?.filter(secret => secret.kind === 'variable')?.map(variable => SecretVariable.from_json(variable));
        container.secret_files = input.secrets?.filter(secret => secret.kind === 'file')?.map(file => SecretFile.from_json(file));

        return container;
    }
}

namespace Container {
    export enum Kind {
        INIT,
        STANDARD
    }
}

class Application {
    name: string;
    namespace: string;
    kind: Application.Kind;
    containers: Container[] = [];

    constructor(namespace, name, kind) {
        this.namespace = namespace;
        this.name = name;
        this.kind = kind;
    }

    static from_json(input): Application {
        const application = new Application(input.namespace, input.name, input.kind);

        for (const container of input.containers) {
            application.containers.push(Container.from_json(container));
        }

        return application;
    }
}

namespace Application {
    export enum Kind {
        DEPLOYMENT,
        STATEFUL_SET
    }
}

class Namespace {
    name: string;
    applications: Application[] = [];

    constructor(name) {
        this.name = name;
    }

    static from_json(input): Namespace {
        const namespace = new Namespace(input.name);
        for (const application of input.applications) {
            namespace.applications.push(Application.from_json(application));
        }

        return namespace;
    }
}


function parse_namespaces_json(namespaces_json) {
    const list: Namespace[] = []

    for (const namespace of namespaces_json) {
        list.push(Namespace.from_json(namespace));
    }

    return list;
}

type Keyvaults = { [name: string]: Keyvault }

function parse_keyvaults_json(keyvaults_json): Keyvaults {
    const result: Keyvaults = {};

    for (const [key, value] of Object.entries(keyvaults_json)) {
        result[key] = Keyvault.from_json(value);
    }
    return result;
}

export {
    parse_namespaces_json, Namespace, Application, Container, SecretVariable, SecretFile
    , KeyvaultKey, Keyvault, parse_keyvaults_json, Keyvaults
}